import { useEffect, useMemo, useState } from 'react';
import { useDataContext } from '../context/data';
import { fetchBanners } from '../utils/heywaitrPlusApi';
import {
  DEFAULT_TRIAL_PERIOD,
  extractTrialPeriod,
  formatTrialPeriod,
} from '../utils/trialPeriod';

// The offer only changes when an admin edits the banners, so one fetch per
// page load is plenty — every consumer (deals page, modals, profile section)
// shares this promise instead of re-hitting /banners.
let trialPeriodPromise = null;
const loadTrialPeriod = () => {
  if (!trialPeriodPromise) {
    trialPeriodPromise = fetchBanners()
      .then((banners) => extractTrialPeriod(banners))
      .catch(() => null);
  }
  return trialPeriodPromise;
};

// Test-only: forget the cached /banners result between test cases.
export const __resetTrialPeriodCache = () => {
  trialPeriodPromise = null;
};

/**
 * The current HeyWaitr+ free-trial period, read live from the backend's
 * /banners offer text (see utils/trialPeriod.js). Returns
 * `{ count, unit, label }` where `label` is localised (e.g. "3 måneder");
 * DEFAULT_TRIAL_PERIOD applies until the fetch resolves or when the backend
 * offer carries no parseable period.
 */
export default function useTrialPeriod() {
  const { language } = useDataContext();
  const [period, setPeriod] = useState(DEFAULT_TRIAL_PERIOD);

  useEffect(() => {
    let active = true;
    loadTrialPeriod().then((p) => {
      if (active && p) setPeriod(p);
    });
    return () => {
      active = false;
    };
  }, []);

  return useMemo(
    () => ({ ...period, label: formatTrialPeriod(period, language) }),
    [period, language],
  );
}
